<script>
export default {
  name: 'Marquee',

  props: {
    paused: {
      type: Boolean,
      default: false,
    },
    reverse: {
      type: Boolean,
      default: false,
    },
    animate: {
      type: Boolean,
      default: true,
    },
    itemGap: {
      type: String,
      default: '0px',
    },
    animationIterationCount: {
      type: String,
      default: 'infinite',
    },
    spaceBetween: {
      type: Number,
      required: false,
      default: null,
    },
    speed: {
      type: [String, Number],
      default: 'normal',
      validator: (val) => ['fast', 'normal', 'slow'].includes(val)
        || (typeof val === 'number' && val > 500 && val < 4000),
    },
  },

  data() {
    return {
      marqueeDiv: null,
      marqueeWidthWrapper: null,
      contentWidth: 0,
      marqueeWidth: 0,
      marqueeWidthWrapperObserver: null,
      marqueeObserver: null,
    };
  },

  computed: {
    spaceBetweenStyle() {
      if (this.spaceBetween) {
        return {
          width: `${this.spaceBetween}px`,
        };
      }

      return {};
    },

    crawlSpeedMilliseconds() {
      // eslint-disable-next-line no-restricted-globals
      if (!isNaN(this.speed)) {
        return this.speed;
      }

      if (this.speed === 'fast') {
        return 1000;
      }

      if (this.speed === 'slow') {
        return 3000;
      }

      return 2000;
    },

    computedWidth() {
      return this.contentWidth + 100000;
    },

    smallContent() {
      return this.contentWidth < this.marqueeWidth;
    },

    offSetForDivDraw() {
      if (this.marqueeWidth === 0 || this.contentWidth === 0) {
        return 0;
      }

      if (!this.animate) {
        return 0;
      }

      if (this.smallContent) {
        return this.contentWidth * 4; // heisenbergCompensator = 4
      }

      return 0;
    },

    repeat() {
      if (!this.animate) {
        return 1;
      }

      if (this.spaceBetween) {
        return 2;
      }

      if (this.marqueeWidth === 0 || this.contentWidth === 0) {
        return 2;
      }

      return this.smallContent
        ? Math.floor(
          (this.marqueeWidth + this.contentWidth) / this.contentWidth,
        ) + 4
        : 2;
    },

    duration() {
      if (this.marqueeWidth === 0 || this.contentWidth === 0) {
        return 10;
      }

      const crawlSpeedPixels = 100;
      const animDistance = this.contentWidth;
      const textAnimationLength = (animDistance * this.crawlSpeedMilliseconds) / crawlSpeedPixels;
      return textAnimationLength / 1000;
    },

    marqueeStyle() {
      return {
        animationDuration: `${this.duration}s`,
        animationDirection: this.reverse ? 'reverse' : 'normal',
        animationIterationCount: this.animationIterationCount,
        gap: this.itemGap,
      };
    },
  },

  mounted() {
    this.marqueeWidthWrapperObserver = new ResizeObserver(
      this.debounce(this.onContentChange, 100),
    );
    this.marqueeObserver = new ResizeObserver(
      this.debounce(this.onMarqueeWidthChange, 100),
    );

    this.marqueeWidthWrapperObserver.observe(
      this.$refs.marqueeWidthWrapper.firstElementChild,
    );
    this.marqueeObserver.observe(this.$refs.marqueeDiv);
  },

  beforeUnmount() {
    this.marqueeWidthWrapperObserver.disconnect();
    this.marqueeObserver.disconnect();
  },

  methods: {
    onContentChange() {
      this.contentWidth = this.$refs.marqueeWidthWrapper.firstElementChild.offsetWidth || 0;
    },

    onMarqueeWidthChange() {
      this.marqueeWidth = this.$refs.marqueeDiv.offsetWidth;
    },

    debounce(fn, delay) {
      let timeoutID;
      return (...args) => {
        if (timeoutID !== null) {
          clearTimeout(timeoutID);
        }
        timeoutID = setTimeout(() => {
          fn(...args);
        }, delay);
      };
    },
  },
};
</script>

<template>
  <div
    v-bind="$attrs"
    ref="marqueeDiv"
    class="tgb-marquee"
  >
    <div
      ref="marqueeWidthWrapper"
      class="tgb-marquee__width-wrapper"
      :class="{
        'tgb-marquee__paused': paused,
      }"
      :style="{
        width: computedWidth + 'px',
        marginLeft: -offSetForDivDraw + 'px',
      }"
    >
      <div
        v-for="n in repeat"
        :key="n"
        :class="[
          animate ? 'tgb-marquee__content--animation' : '',
          'tgb-marquee__content-repeater',
        ]"
        :style="marqueeStyle"
      >
        <div
          v-if="animate"
          :style="spaceBetweenStyle"
        />
        <slot />
      </div>
    </div>
  </div>
</template>

<style scoped>
.tgb-marquee {
  overflow: hidden;
  height: inherit;
  align-items: inherit;
  display: inline-grid;
}

.tgb-marquee__content--animation {
  animation-name: marquee-animation;
  animation-timing-function: linear;
}

.tgb-marquee__paused .tgb-marquee__content--animation {
  animation-play-state: paused;
}

.tgb-marquee__content-repeater {
  display: inline-flex;
}

@keyframes marquee-animation {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-100%);
  }
}
</style>
